/**
 * assetRegistry — unified, indexed view over every asset type the content
 * browser displays. Phase 1: a thin aggregator on top of CustomAssetManager
 * that adds a virtual `path: string[]` field per asset, persisted to
 * localStorage as a write-through cache until the server grows the column.
 *
 * Sources merged here (each tagged with `kind`):
 *   - animation_assets (split into kind: 'body_anim' | 'face_anim')
 *   - audio_assets     (kind: 'audio')
 *   - prefabs          (kind: 'prefab')
 *   - rigs / textures  (not yet — Phase 1.1)
 *
 * Listener pattern matches CustomAssetManager.subscribe — UI subscribes,
 * gets re-rendered on every notify.
 *
 * Folder paths: the cache key is per-character + per-asset
 * (`assetRegistry:{characterId}` → `{ assetId: ["folder", "subfolder"] }`).
 * Tagged with `_authoredAgainst` (the API target) per the UX rule in
 * CLAUDE.md — when the backend changes, the shadow is surfaced as
 * `stale-backend` rather than silently merged.
 */

import { CustomAssetManager } from './CustomAssetManager'
import { API_TARGET } from './devServer'
import { ogsRecordFromRow } from './ogsAssetRecord'
import {
  hasServerFolder,
  serverFolderOf,
  folderPathFromSegments,
  segmentsFromFolderPath,
  folderUpdaterFor,
  verifyFolderWrite,
} from './assetFolderPath'
import { readTags, tagUpdaterFor, tagsEqual, verifyTagWrite } from './assetTags'
import { validateFolderName } from './hiddenFolders'

const LS_PREFIX = 'assetRegistry:'
let _characterId = null
let _listeners = []
let _folderShadow = { _authoredAgainst: null, paths: {} } // { assetId -> string[] }
let _staleBackend = false
// Per-character rig info — surfaced as a single kind:'rig' asset card so
// the content browser inspector can show its mesh slots + drop targets.
let _rigInfo = null // { rigPath, slug } | null
// Last-selected raw asset id per kind. Used by the Unreal-style ← "use
// selected" button on assignable slots (material slots on the rig,
// texture slots inside materials). Lives outside React state so the
// memory survives the inspector swap when the user clicks the rig card.
let _lastSelectedByKind = { material: null, texture: null }
// Per-asset move state, so a folder move that did not persist is visible on the
// card instead of being a console line nobody reads.
// { [assetId]: { state: 'saving' | 'error', message?: string } }
let _moveState = {}
// One console.error per asset for a row that states no folder_path. list() runs
// on every render, so an undeduplicated report would drown the console it is
// trying to be noticed in.
const _missingFolderReported = new Set()

function notify() {
  for (const fn of _listeners) fn()
}

function lsKey() {
  return `${LS_PREFIX}${_characterId ?? 'default'}`
}

function loadShadow() {
  if (typeof localStorage === 'undefined') return { _authoredAgainst: API_TARGET, paths: {} }
  try {
    const raw = localStorage.getItem(lsKey())
    if (!raw) return { _authoredAgainst: API_TARGET, paths: {} }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.paths) {
      return { _authoredAgainst: API_TARGET, paths: {} }
    }
    return parsed
  } catch (e) {
    console.warn('[assetRegistry] failed to load folder shadow:', e)
    return { _authoredAgainst: API_TARGET, paths: {} }
  }
}

function saveShadow() {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(lsKey(), JSON.stringify({
      _authoredAgainst: API_TARGET,
      _savedAt: Date.now(),
      paths: _folderShadow.paths,
    }))
  } catch (e) {
    console.warn('[assetRegistry] failed to persist folder shadow:', e)
  }
}

function recordsFromCAM() {
  const out = []
  for (const a of CustomAssetManager.listAnimationAssets()) {
    out.push({
      id: `anim:${a.id}`,
      rawId: a.id,
      kind: a.kind === 'face' ? 'face_anim' : 'body_anim',
      name: a.name ?? `clip_${a.id}`,
      url: a.file_url,
      additive: !!a.additive,
      loopable: !!a.loopable,
      durationMs: a.duration_ms ?? null,
      category: a.category ?? 'other',
      // ENG-442 Unreal-parity additive settings (server defaults: none/none/0).
      additiveType: a.additiveType ?? 'none',
      basePoseType: a.basePoseType ?? 'none',
      basePoseAssetId: a.basePoseAssetId ?? null,
      refFrameIndex: a.refFrameIndex ?? 0,
      serverFolderPath: serverFolderOf(a),
      raw: a,
    })
  }
  for (const a of CustomAssetManager.listAudioAssets()) {
    out.push({
      id: `audio:${a.id}`,
      rawId: a.id,
      kind: 'audio',
      name: a.name ?? `audio_${a.id}`,
      url: a.file_url,
      transcript: a.transcript ?? '',
      durationMs: a.duration_ms ?? null,
      serverFolderPath: serverFolderOf(a),
      raw: a,
    })
  }
  for (const p of CustomAssetManager.listPrefabs()) {
    out.push({
      id: `prefab:${p.id}`,
      rawId: p.id,
      kind: 'prefab',
      name: p.display_name ?? p.tag ?? `prefab_${p.id}`,
      tag: p.tag,
      serverFolderPath: serverFolderOf(p),
      raw: p,
    })
  }
  for (const t of CustomAssetManager.listTextureAssets()) {
    out.push({
      id: `texture:${t.id}`,
      rawId: t.id,
      kind: 'texture',
      name: t.name ?? `texture_${t.id}`,
      url: t.file_url,
      width: t.width ?? null,
      height: t.height ?? null,
      format: t.format ?? null,
      byteSize: t.byte_size ?? null,
      serverFolderPath: serverFolderOf(t),
      raw: t,
    })
  }
  if (_rigInfo) {
    out.push({
      id: `rig:${_rigInfo.slug}`,
      rawId: _rigInfo.slug,
      kind: 'rig',
      name: 'Rig',
      url: _rigInfo.rigPath,
      mapping: CustomAssetManager.listMaterialMapping(),
      serverFolderPath: '/',
      raw: _rigInfo,
    })
  }
  for (const e of CustomAssetManager.listEnvironmentAssets()) {
    out.push({
      id: `environment:${e.id}`,
      rawId: e.id,
      kind: 'environment',
      name: e.name ?? `environment_${e.id}`,
      url: e.file_url,
      format: e.format ?? null,
      byteSize: e.byte_size ?? e.byteSize ?? null,
      defaultPose: e.default_pose ?? e.defaultPose ?? {},
      serverFolderPath: serverFolderOf(e),
      // ENG-561 bake state + provenance (for the card badge + inspector).
      bakeStatus: e.bakeStatus ?? e.bake_status ?? 'none',
      bakedAt: e.bakedAt ?? e.baked_at ?? null,
      bakedBy: e.bakedBy ?? e.baked_by ?? null,
      raw: e,
    })
  }
  for (const g of CustomAssetManager.listGarmentAssets()) {
    out.push({
      id: `garment:${g.id}`,
      rawId: g.id,
      kind: 'garment',
      name: g.name ?? `garment_${g.id}`,
      // The server's own URL, taken verbatim — already the same-origin proxy
      // path. Never rebuilt here (ENG-999).
      url: g.fileUrl ?? g.file_url,
      format: g.format ?? null,
      byteSize: g.byteSize ?? g.byte_size ?? null,
      version: g.version ?? null,
      metadata: g.metadata ?? {},
      serverFolderPath: serverFolderOf(g),
      raw: g,
    })
  }
  for (const o of CustomAssetManager.listOgsAssets()) {
    const rec = ogsRecordFromRow(o)
    if (rec) out.push(rec)
  }
  for (const b of CustomAssetManager.listBlueprintAssets()) {
    // graphKind is the editor surface this row IS — body | face | events. One
    // row is one graph (ENG-1030), so the card opens exactly one editor, the
    // same three the outliner already lists.
    const graphKind = b.kind ?? null
    out.push({
      id: `blueprint_asset:${b.id}`,
      rawId: b.id,
      kind: 'blueprint_asset',
      graphKind,
      name: b.name ?? `blueprint_${b.id}`,
      // isDefault is DATA on the row, not something the browser infers by
      // comparing graphs — the card and inspector both read it straight
      // through. It is per KIND: a character has a default body graph, a
      // default face graph and a default event graph.
      isDefault: !!(b.isDefault ?? b.is_default),
      version: b.version ?? null,
      updatedAt: b.updatedAt ?? b.updated_at ?? null,
      createdBy: b.createdBy ?? b.created_by ?? '',
      // serverFolderOf, with no invented default: a hardcoded '/blueprints'
      // here would answer for every row whose folder the API failed to state,
      // which is precisely what hid the material/texture folder bug for months.
      // The endpoint dual-emits folder_path, so nothing is lost.
      serverFolderPath: serverFolderOf(b),
      raw: b,
    })
  }
  for (const m of CustomAssetManager.listMaterials()) {
    out.push({
      id: `material:${m.id}`,
      rawId: m.id,
      kind: 'material',
      name: m.name ?? `material_${m.id}`,
      // No default: a row whose type the server did not state reaches the badge
      // as absent (materialBadge -> '?'), rather than being invented as a
      // legacy pbr row and silently badged LEGACY.
      materialType: m.type,
      data: m.data ?? {},
      serverFolderPath: serverFolderOf(m),
      raw: m,
    })
  }
  // Tags, resolved in ONE place for every kind (ENG-1132) rather than at each
  // of the eleven push sites above. readTags always answers an array, so no
  // consumer has to guard a row written before the tags migration -- and a kind
  // added later gets tags without anyone remembering to add the line.
  for (const r of out) r.tags = readTags(r.raw)
  return out
}

export const assetRegistry = {

  // Wire to the active character. Must be called whenever the character
  // changes — folder-shadow keys are per-character.
  setCharacter(characterId) {
    _characterId = characterId
    _folderShadow = loadShadow()
    _staleBackend = !!_folderShadow._authoredAgainst &&
                    _folderShadow._authoredAgainst !== API_TARGET
    notify()
  },

  // Surface the character's rig as a kind:'rig' asset card. Called from
  // ContentBrowser once the character record is available.
  setRigInfo({ slug, rigPath } = {}) {
    _rigInfo = slug ? { slug, rigPath: rigPath ?? '' } : null
    notify()
  },

  // Track the most-recently-selected raw asset id per kind, so the ←
  // "use selected" button on slot fields can grab it. Only kinds with
  // an assignable target (material, texture) are tracked.
  setLastSelected(kind, rawId) {
    if (!kind) return
    _lastSelectedByKind = { ..._lastSelectedByKind, [kind]: rawId ?? null }
  },
  getLastSelected(kind) {
    return _lastSelectedByKind[kind] ?? null
  },

  // True when the localStorage shadow was authored against a different API
  // target than the current one. UI should show a stale-backend banner and
  // offer Apply / Discard rather than silently merging.
  isStaleBackend() { return _staleBackend },

  // One-shot migration of legacy localStorage folder shadow entries onto
  // the server (folder_path column). Called once per character load, after
  // CustomAssetManager.init has populated the asset lists.
  //
  // For each shadow entry:
  //   - if the asset is not server-backed (legacy slot texture, deleted),
  //     leave the shadow as-is.
  //   - if shadow path == server path, clear the redundant shadow.
  //   - otherwise PATCH the server with the user's path and clear the shadow.
  //
  // Skips `__empty__` (purely local "create empty folder" markers).
  async migrateShadowToServer() {
    if (typeof window === 'undefined') return
    const records = recordsFromCAM()
    const byId = new Map(records.map(r => [r.id, r]))
    let mutated = false
    for (const [assetId, path] of Object.entries(_folderShadow.paths)) {
      if (assetId === '__empty__') continue
      if (!Array.isArray(path)) continue
      const r = byId.get(assetId)
      if (!r) continue                       // asset deleted; keep shadow harmless
      if (!r.serverFolderPath) continue      // legacy slot texture; no server path
      const shadowPath = '/' + path.join('/')
      if (shadowPath === r.serverFolderPath) {
        delete _folderShadow.paths[assetId]
        mutated = true
        continue
      }
      // A shadow path of "/" (root) was almost certainly a stale localStorage
      // entry from before the server learned about folder_path — don't push
      // root over a non-root server default like /animations/body. Clear the
      // shadow but leave the server intact. (Genuine "move to root" can be
      // re-done via drag-drop, which writes a real PATCH.)
      if (shadowPath === '/' && r.serverFolderPath !== '/') {
        delete _folderShadow.paths[assetId]
        mutated = true
        continue
      }
      // PATCH server with the user's manual move. The shadow is dropped only
      // once the server echoes the folder we asked for — dropping it on a bare
      // 200 is what turned a silently-ignored PATCH field into data loss.
      try {
        const cam = (await import('./CustomAssetManager')).CustomAssetManager
        const row = await folderUpdaterFor(r.kind)(cam, r.rawId, { folder_path: shadowPath })
        const verdict = verifyFolderWrite(shadowPath, row)
        if (!verdict.ok) {
          console.error(
            `[assetRegistry] migrate ${r.kind} "${r.name}" to ${shadowPath}: ${verdict.message}. ` +
            `Keeping the local shadow so the move is not lost.`,
          )
          _moveState[assetId] = { state: 'error', message: verdict.message }
          notify()
          continue
        }
        delete _folderShadow.paths[assetId]
        delete _moveState[assetId]
        mutated = true
      } catch (e) {
        const message = e?.message ?? String(e)
        console.error(`[assetRegistry] migrate ${r.kind} "${r.name}" to ${shadowPath}: ${message}`)
        _moveState[assetId] = { state: 'error', message }
        notify()
      }
    }
    if (mutated) {
      saveShadow()
      notify()
    }
  },

  // Resolve and clear the stale-backend state (user clicked Apply or Discard).
  resolveStaleBackend(action) {
    if (action === 'discard') {
      _folderShadow = { _authoredAgainst: API_TARGET, paths: {} }
    } else {
      _folderShadow = { ..._folderShadow, _authoredAgainst: API_TARGET }
    }
    _staleBackend = false
    saveShadow()
    notify()
  },

  // Returns a flat AssetRecord[] for the current character, with paths
  // joined from the localStorage shadow. Asset records are derived from
  // CustomAssetManager — subscribe to changes via subscribe(), do NOT
  // mutate the returned objects.
  list() {
    const records = recordsFromCAM()
    for (const r of records) {
      // Server folder_path wins until the user explicitly moves the asset, at
      // which point the localStorage shadow takes over until the move is
      // confirmed persisted (see moveAsset).
      const shadow = _folderShadow.paths[r.id]
      if (shadow != null) {
        r.path = shadow
      } else if (typeof r.serverFolderPath === 'string') {
        // "" and "/" are both the root — a real location, not a missing value.
        r.path = segmentsFromFolderPath(r.serverFolderPath)
      } else {
        // The row states no folder. There is deliberately no hardcoded default
        // here: substituting one is what hid the folder_path casing bug, since
        // the invented answer was indistinguishable from a real server folder.
        // Show it at root and SAY so.
        r.path = []
        if (hasServerFolder(r)) {
          r.folderState = 'error'
          r.folderMessage = `Server row states no folder_path — showing at root`
          if (!_missingFolderReported.has(r.id)) {
            _missingFolderReported.add(r.id)
            // Deferred out of the render pass: list() is called from component
            // render, and ConsoleStrip subscribes to console, so reporting
            // synchronously here is a setState-during-render of a different
            // component. A diagnostic does not care about a tick of latency.
            const { kind, name, id } = r
            setTimeout(() => {
              console.error(
                `[assetRegistry] ${kind} "${name}" (${id}): the server row states no folder_path. ` +
                `Showing it at root rather than inventing a folder. ` +
                `Check that the API dual-emits folder_path for this asset kind.`,
              )
            }, 0)
          }
        }
      }
      const mv = _moveState[r.id]
      if (mv) {
        r.moveState = mv.state
        r.moveMessage = mv.message
      }
    }
    return records
  },

  // Move an asset into a folder AND persist it, loudly.
  //
  // The optimistic localStorage shadow is written first so the card moves under
  // the cursor, and it is dropped ONLY once the server echoes the folder we
  // asked for (verifyFolderWrite). That ordering is the fix for the original
  // bug: the previous code deleted the shadow on the strength of a 200, and the
  // PATCH that answered 200 had bound the folder field to nothing, so the only
  // record of the user's move was destroyed.
  //
  // Returns { ok, reason?, message? }. Never throws — the caller is a drop
  // handler — but a failure is reported three ways: console.error, a persisted
  // shadow (so the move is not lost), and an 'error' badge on the card.
  async moveAsset(assetId, path) {
    if (!Array.isArray(path)) path = []
    // assetRegistry, not `this`: moveAsset is passed straight to a drop
    // handler as a callback, which would arrive with `this` unbound.
    const record = assetRegistry.list().find(a => a.id === assetId)
    if (!record) {
      console.error(`[assetRegistry] moveAsset: no such asset "${String(assetId)}"`)
      return { ok: false, reason: 'unknown-asset', message: 'No such asset' }
    }

    const folderPath = folderPathFromSegments(path)

    // Local-only asset (a legacy per-slot texture, the rig card): there is no
    // server row to move, so the shadow IS the storage. Not a failure.
    if (!hasServerFolder(record)) {
      _folderShadow.paths[assetId] = path
      saveShadow()
      notify()
      return { ok: true, reason: 'local-only' }
    }

    _folderShadow.paths[assetId] = path
    _moveState[assetId] = { state: 'saving' }
    saveShadow()
    notify()

    const fail = (reason, message) => {
      console.error(`[assetRegistry] move ${record.kind} "${record.name}" to ${folderPath}: ${message}`)
      _moveState[assetId] = { state: 'error', message }
      // Shadow deliberately kept: it is now the only record of the move.
      saveShadow()
      notify()
      return { ok: false, reason, message }
    }

    let row
    try {
      const cam = (await import('./CustomAssetManager')).CustomAssetManager
      row = await folderUpdaterFor(record.kind)(cam, record.rawId, { folder_path: folderPath })
    } catch (e) {
      return fail('request-failed', e?.message ?? String(e))
    }

    const verdict = verifyFolderWrite(folderPath, row)
    if (!verdict.ok) return fail(verdict.reason, verdict.message)

    // Confirmed. CustomAssetManager has already replaced its cached row with
    // the server's response, so the record now reports the new folder on its
    // own and the shadow would only shadow the truth.
    delete _folderShadow.paths[assetId]
    delete _moveState[assetId]
    saveShadow()
    notify()
    return { ok: true, reason: null }
  },

  /**
   * Set an asset's tags (ENG-1132) — the ONE entry point for "the user changed
   * this asset's tags", whoever asked: the inspector's editor, a context menu,
   * a future multi-select.
   *
   * Deliberately NOT optimistic and with no localStorage shadow, unlike
   * moveAsset. A folder move is a drag whose result the user is staring at, so
   * it shows immediately and reconciles; tags are a small explicit edit against
   * a server column, and a shadow would be a second source of truth for data
   * nobody is mid-drag with. CustomAssetManager replaces its cached row with
   * the server's response, so a confirmed write is visible on the next render.
   *
   * A 200 is not proof: the echo is compared (verifyTagWrite) before this
   * reports success. That is the folder_path lesson — a PATCH answered 200 for
   * months with the field bound to nothing, and the client believed it.
   */
  async setAssetTags(assetId, tags) {
    const record = assetRegistry.list().find(a => a.id === assetId)
    if (!record) {
      console.error(`[assetRegistry] setAssetTags: no such asset "${String(assetId)}"`)
      return { ok: false, reason: 'unknown-asset', message: 'No such asset' }
    }
    const next = readTags({ tags })
    if (tagsEqual(next, record.tags ?? [])) return { ok: true, reason: 'unchanged' }

    let updater
    try {
      // Throws for a kind with nowhere to store a tag, rather than reporting a
      // save that never happened.
      updater = tagUpdaterFor(record.kind)
    } catch (e) {
      const message = e?.message ?? String(e)
      console.error(`[assetRegistry] ${message}`)
      return { ok: false, reason: 'untaggable', message }
    }

    const fail = (reason, message) => {
      console.error(`[assetRegistry] tag ${record.kind} "${record.name}": ${message}`)
      return { ok: false, reason, message }
    }

    let row
    try {
      const cam = (await import('./CustomAssetManager')).CustomAssetManager
      row = await updater(cam, record.rawId, { tags: next })
    } catch (e) {
      return fail('request-failed', e?.message ?? String(e))
    }

    const verdict = verifyTagWrite(next, row)
    if (!verdict.ok) {
      return fail(verdict.reason, verdict.reason === 'not-reported'
        ? 'the server did not report the tags back, so the write cannot be confirmed'
        : `the server stored ${JSON.stringify(verdict.reported)}`)
    }
    notify()
    return { ok: true, reason: null, tags: next }
  },

  // Clear a stuck move error (the user retries, or dismisses it).
  clearMoveState(assetId) {
    if (!_moveState[assetId]) return
    delete _moveState[assetId]
    notify()
  },

  // Returns the set of folder paths in use, derived from the asset list +
  // any "empty" folders the user created. Empty folders also live in the
  // shadow under a reserved key (paths['__empty__']: [['Foo'], ['Foo','Bar']]).
  listFolders() {
    const seen = new Set()
    seen.add('') // root
    const addPath = (path) => {
      if (!Array.isArray(path)) return
      for (let i = 1; i <= path.length; i++) {
        seen.add(path.slice(0, i).join('/'))
      }
    }
    // localStorage-shadowed paths
    for (const [id, path] of Object.entries(_folderShadow.paths)) {
      if (id === '__empty__') continue
      addPath(path)
    }
    // Empty folders: each entry IS a path[].
    for (const p of _folderShadow.paths.__empty__ ?? []) addPath(p)
    // Server-derived folder_path on materials/textures: pull from every
    // record's resolved path so freshly-created assets surface their
    // home folder in the tree.
    for (const r of this.list()) addPath(r.path)
    return Array.from(seen).sort()
  },

  // Write the localStorage shadow ONLY — no server write, no verification.
  //
  // This is the low-level primitive. Anything the user perceives as "move this
  // asset" must call moveAsset instead: a shadow-only move looks identical on
  // screen and is invisible to every other editor, which is how the
  // multi-select Move-to picker came to not persist at all.
  setPath(assetId, path) {
    if (!Array.isArray(path)) path = []
    _folderShadow.paths[assetId] = path
    saveShadow()
    notify()
  },

  // Create a new empty folder. Empty folders only exist in the shadow until
  // an asset is moved into them.
  createFolder(path) {
    if (!Array.isArray(path) || path.length === 0) return
    // Dot-prefixed names are the reserved hidden namespace (hiddenFolders.js).
    // The UI refuses with a dialog before reaching here; this guard is defense
    // in depth so no other caller can mint one silently.
    const invalid = path.map(validateFolderName).find(v => !v.ok)
    if (invalid) {
      console.error(`[assetRegistry] createFolder /${path.join('/')}: ${invalid.message}`)
      return
    }
    const empties = _folderShadow.paths.__empty__ ?? []
    const key = path.join('/')
    if (empties.some(p => p.join('/') === key)) return
    _folderShadow.paths.__empty__ = [...empties, path]
    saveShadow()
    notify()
  },

  // Rename a folder. Folders are virtual — they exist as long as some asset
  // has a `folder_path` that contains them. To rename, we walk every asset
  // whose path is rooted at oldPath and rewrite both the in-memory record
  // and the server row (PATCH /api/{kind}/{id}). PlayCanvas treats folders
  // as a tree of folder-typed assets; we don't have folder rows, so the
  // rename is a bulk move of the children instead. Empty-folder records
  // (localStorage-only markers) are rewritten in the same pass.
  async renameFolder(oldPath, newName) {
    if (!Array.isArray(oldPath) || oldPath.length === 0 || !newName) return
    // Same reserved-namespace guard as createFolder (hiddenFolders.js).
    const verdict = validateFolderName(newName)
    if (!verdict.ok) {
      console.error(`[assetRegistry] renameFolder → "${newName}": ${verdict.message}`)
      return
    }
    const oldPrefix = '/' + oldPath.join('/')
    const newPath = [...oldPath.slice(0, -1), newName]
    const newPrefix = '/' + newPath.join('/')

    // Locate every asset whose resolved path is under oldPath and PATCH
    // its server folder_path. Skip legacy slot textures (no UUID rawId).
    const cam = (await import('./CustomAssetManager')).CustomAssetManager
    const records = recordsFromCAM()
    const moves = []
    for (const r of records) {
      // Compute current resolved path (shadow wins over server, mirrors list()).
      const shadow = _folderShadow.paths[r.id]
      let resolved
      if (Array.isArray(shadow)) {
        resolved = '/' + shadow.join('/')
      } else if (r.serverFolderPath) {
        resolved = r.serverFolderPath
      } else {
        continue // legacy texture or asset with no folder concept
      }
      if (resolved !== oldPrefix && !resolved.startsWith(oldPrefix + '/')) continue
      const newFolderPath = newPrefix + resolved.slice(oldPrefix.length)
      moves.push({ r, newFolderPath })
    }

    for (const { r, newFolderPath } of moves) {
      if (!hasServerFolder(r)) {
        // Local-only (legacy per-slot texture): re-point the shadow instead.
        _folderShadow.paths[r.id] = segmentsFromFolderPath(newFolderPath)
        continue
      }
      try {
        const row = await folderUpdaterFor(r.kind)(cam, r.rawId, { folder_path: newFolderPath })
        const verdict = verifyFolderWrite(newFolderPath, row)
        if (!verdict.ok) {
          console.error(`[assetRegistry] rename: ${r.kind} "${r.name}" → ${newFolderPath}: ${verdict.message}`)
          // Keep the child under the NEW name locally so the tree is not torn
          // in half, and badge it so the un-persisted one is identifiable.
          _folderShadow.paths[r.id] = segmentsFromFolderPath(newFolderPath)
          _moveState[r.id] = { state: 'error', message: verdict.message }
          continue
        }
        // Confirmed. Drop the now-redundant shadow entry — it pointed at the
        // OLD path and would re-shadow the move on reload otherwise.
        delete _folderShadow.paths[r.id]
        delete _moveState[r.id]
      } catch (e) {
        const message = e?.message ?? String(e)
        console.error(`[assetRegistry] rename: ${r.kind} "${r.name}" → ${newFolderPath}: ${message}`)
        _folderShadow.paths[r.id] = segmentsFromFolderPath(newFolderPath)
        _moveState[r.id] = { state: 'error', message }
      }
    }

    // Rewrite localStorage empty-folder markers.
    _folderShadow.paths.__empty__ = (_folderShadow.paths.__empty__ ?? []).map(p => {
      const prefix = p.slice(0, oldPath.length).join('/')
      return prefix === oldPath.join('/') ? [...newPath, ...p.slice(oldPath.length)] : p
    })

    saveShadow()
    notify()
  },

  // Delete a folder AND the assets inside it (recursively, including subfolders).
  // Destructive: each child asset is deleted on the server (DB + S3), not
  // re-parented. The caller (FolderTree) must confirm with the user first.
  async deleteFolder(path) {
    if (!Array.isArray(path) || path.length === 0) return
    const oldPrefix = '/' + path.join('/')
    const cam = (await import('./CustomAssetManager')).CustomAssetManager
    const records = recordsFromCAM()
    for (const r of records) {
      const shadow = _folderShadow.paths[r.id]
      let resolved
      if (Array.isArray(shadow)) resolved = '/' + shadow.join('/')
      else if (r.serverFolderPath) resolved = r.serverFolderPath
      else continue
      if (resolved !== oldPrefix && !resolved.startsWith(oldPrefix + '/')) continue
      try {
        if (r.kind === 'material')      await cam.deleteMaterial(r.rawId)
        else if (r.kind === 'texture' && !String(r.rawId).includes('.'))
                                        await cam.deleteTextureAsset(r.rawId)
        else if (r.kind === 'body_anim' || r.kind === 'face_anim')
                                        await cam.deleteAnimationAsset(r.rawId)
        else if (r.kind === 'audio')    await cam.deleteAudioAsset(r.rawId)
        else if (r.kind === 'prefab')   await cam.deletePrefab(r.rawId)
        // The character's DEFAULT blueprint is refused by the server (409) — it
        // is what the character loads. The catch below logs it and the card
        // stays, which is the honest outcome: nothing silently vanishes.
        else if (r.kind === 'blueprint_asset') await cam.deleteBlueprintAsset(r.rawId)
        delete _folderShadow.paths[r.id]
      } catch (e) {
        console.warn(`[assetRegistry] deleteFolder: failed to delete ${r.id}:`, e)
      }
    }
    _folderShadow.paths.__empty__ = (_folderShadow.paths.__empty__ ?? []).filter(p =>
      p.slice(0, path.length).join('/') !== path.join('/')
    )
    saveShadow()
    notify()
  },

  subscribe(fn) {
    _listeners.push(fn)
    return () => { _listeners = _listeners.filter(l => l !== fn) }
  },
}

// Bridge CustomAssetManager changes through to our listeners so consumers
// of assetRegistry don't also have to subscribe to CAM.
if (typeof window !== 'undefined') {
  CustomAssetManager.subscribe(() => notify())
  window.__ASSET_REGISTRY__ = assetRegistry
}
